import {
  ChangeDetectionStrategy,
  Component,
  Input,
  inject,
} from '@angular/core';
import { EmailVerification } from '@freelancer/email-verification';
import { BannerAlertType } from '@freelancer/ui/banner-alert';
import { ButtonColor } from '@freelancer/ui/button';
import { ToastAlertService, ToastAlertType } from '@freelancer/ui/toast-alert';
import { ErrorCodeApi } from 'api-typings/errors/errors';

@Component({
  standalone: false,
  selector: 'app-email-verification-banner-alert',
  template: `
    <fl-card
      class="mb-xsmall"
      [edgeToEdge]="true"
    >
      <fl-banner-alert
        flTrackingSection="VerifyEmailProjectViewBanner"
        bannerTitle="Email verification required"
        i18n-bannerTitle="Email verification required"
        [type]="BannerAlertType.INFO"
        [closeable]="false"
      >
        <p class="mb-mid">
          <ng-content></ng-content>
        </p>
        <div>
          <fl-button
            class="EmailVerificationBannerButton mr-small mb-xxsmall"
            i18n="Resend email button"
            flTrackingLabel="ResendEmailButton"
            [color]="resendEmailButtonColor"
            (click)="resendVerificationEmail()"
          >
            Resend Email
          </fl-button>
          <fl-button
            class="EmailVerificationBannerButton mr-small mb-xxsmall"
            i18n="Change email button"
            flTrackingLabel="ChangeEmailButton"
            [color]="changeEmailButtonColor"
            [link]="'/users/change-password-email.php'"
          >
            Change Email Address
          </fl-button>
          @if (contactButtonEnabled) {
            <fl-button
              class="EmailVerificationBannerButton"
              i18n="Contact us button"
              flTrackingLabel="ContactUsButton"
              [color]="contactUsButtonColor"
              [link]="'/support/profile/verifying-my-email-address'"
            >
              Contact Us
            </fl-button>
          }
        </div>
      </fl-banner-alert>
    </fl-card>
  `,
  styleUrls: ['./email-verification-banner-alert.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EmailVerificationBannerAlertComponent {
  private emailVerify = inject(EmailVerification);
  private toastAlertService = inject(ToastAlertService);

  BannerAlertType = BannerAlertType;

  @Input() resendEmailButtonColor = ButtonColor.SECONDARY;
  @Input() changeEmailButtonColor = ButtonColor.DEFAULT;
  @Input() contactUsButtonColor = ButtonColor.DEFAULT;

  @Input() contactButtonEnabled = true;
  @Input() emailVerifyGoto?: string;

  resendVerificationEmail(): void {
    this.emailVerify.send(this.emailVerifyGoto).then(response => {
      if (response.status === 'success') {
        this.toastAlertService.open({
          type: ToastAlertType.SUCCESS,
          message: $localize`Verification email has been resent.`,
        });
      } else if (
        response.status === 'error' &&
        response.errorCode === ErrorCodeApi.EMAIL_ALREADY_VERIFIED
      ) {
        this.toastAlertService.open({
          type: ToastAlertType.ERROR,
          message: $localize`You have already verified your email address.`,
        });
      }
    });
  }
}
